import React, { useState, useEffect } from 'react';
import { flushSync } from 'react-dom';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { ServicesSection } from './components/ServicesSection';
import { TrainingCatalog } from './components/TrainingCatalog';
import { VerificationPortal } from './components/VerificationPortal';
import { AboutSection } from './components/AboutSection';
import { TestimonialsSection } from './components/TestimonialsSection';
import { FAQSection } from './components/FAQSection';
import { ContactSection } from './components/ContactSection';
import { Footer } from './components/Footer';
import { WhatsAppButton } from './components/WhatsAppButton';
import { LegalModal } from './components/LegalModal';
import { ScrollToTopButton } from './components/ScrollToTopButton';
import { Language, ThemeMode } from './types';

import { getSiteBranding } from './services/siteSettingsStorage';

export default function App() {
  const [currentLang, setCurrentLang] = useState<Language>(() => {
    const saved = localStorage.getItem('chc_lang') as Language;
    return (saved === 'fr' || saved === 'en' || saved === 'ar') ? saved : 'fr';
  });
  const [theme, setTheme] = useState<ThemeMode>(() => {
    const saved = localStorage.getItem('chc_theme') as ThemeMode;
    return saved || 'light';
  });
  
  // Preloader removal
  useEffect(() => {
    const preloader = document.getElementById('chc-preloader');
    if (preloader) {
      setTimeout(() => {
        preloader.style.opacity = '0';
        preloader.style.visibility = 'hidden';
        setTimeout(() => {
          preloader.remove();
        }, 500);
      }, 300);
    }
  }, []);

  const [activeSection, setActiveSection] = useState('accueil');
  const [prefilledService, setPrefilledService] = useState<string>('');
  const [initialVerifyId, setInitialVerifyId] = useState<string>('');
  const [legalModalType, setLegalModalType] = useState<'privacy' | 'terms' | null>(null);

  // Synchronize favicon
  useEffect(() => {
    const updateFavicon = (url: string) => {
      let link = document.querySelector("link[rel~='icon']") as HTMLLinkElement;
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = url;
    };

    const branding = getSiteBranding();
    if (branding.faviconUrl) {
      updateFavicon(branding.faviconUrl);
    }

    const handleBrandingUpdate = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail && customEvent.detail.faviconUrl) {
        updateFavicon(customEvent.detail.faviconUrl);
      } else {
        updateFavicon('/vite.svg'); // Fallback default
      }
    };
    
    window.addEventListener('chc_site_branding_updated', handleBrandingUpdate);
    return () => window.removeEventListener('chc_site_branding_updated', handleBrandingUpdate);
  }, []);

  // Synchronize theme with DOM root and local storage
  useEffect(() => {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('chc_theme', theme);
  }, [theme]);

  // Handle URL parameters for direct QR code scanning/verification links
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const verifyId = urlParams.get('verify') || urlParams.get('cert');
    if (verifyId) {
      setInitialVerifyId(verifyId);
      setTimeout(() => {
        const el = document.getElementById('verifier');
        if (el) {
          el.scrollIntoView({ behavior: 'smooth' });
        }
      }, 300);
    }
  }, []);

  // Update HTML document attributes and localStorage when language changes (RTL for Arabic)
  useEffect(() => {
    localStorage.setItem('chc_lang', currentLang);
    document.documentElement.lang = currentLang;
    document.documentElement.dir = currentLang === 'ar' ? 'rtl' : 'ltr';
  }, [currentLang]);

  // Smooth scroll to section
  const handleNavigate = (sectionId: string) => {
    setActiveSection(sectionId);

    if (sectionId === 'admin') {
      window.location.href = '/_sysadmin';
      return;
    }

    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleQuickVerify = (certId: string) => {
    setInitialVerifyId(certId);
    handleNavigate('verifier');
  };

  const handleSelectServiceForDevis = (serviceTitle: string) => {
    setPrefilledService(serviceTitle);
    handleNavigate('contact');
  };

  const toggleTheme = (e?: React.MouseEvent) => {
    const nextTheme = theme === 'dark' ? 'light' : 'dark';

    // Type casting to bypass TS complaining about experimental API
    const doc = document as any;

    if (!doc.startViewTransition || !e) {
      setTheme(nextTheme);
      return;
    }

    const x = e.clientX;
    const y = e.clientY;
    const endRadius = Math.hypot(
      Math.max(x, innerWidth - x),
      Math.max(y, innerHeight - y)
    );

    const transition = doc.startViewTransition(() => {
      flushSync(() => {
        setTheme(nextTheme);
      });
    });

    transition.ready.then(() => {
      const clipPath = [
        `circle(0px at ${x}px ${y}px)`,
        `circle(${endRadius}px at ${x}px ${y}px)`
      ];

      document.documentElement.animate(
        {
          clipPath: nextTheme === 'dark' ? clipPath : [...clipPath].reverse(),
        },
        {
          duration: 500,
          easing: 'ease-in-out',
          pseudoElement: nextTheme === 'dark' ? '::view-transition-new(root)' : '::view-transition-old(root)',
        }
      );
    });
  };

  return (
    <div dir={currentLang === 'ar' ? 'rtl' : 'ltr'} className={`min-h-screen bg-slate-100 dark:bg-slate-950 text-slate-900 dark:text-slate-100 flex flex-col font-sans transition-colors duration-200 ${currentLang === 'ar' ? 'font-cairo' : 'font-inter'}`}>
      
      {/* Top Main Navigation */}
      <Header
        currentLang={currentLang}
        onLanguageChange={setCurrentLang}
        activeSection={activeSection}
        onNavigate={handleNavigate}
        isAdminLoggedIn={false}
        onOpenAdmin={() => window.location.href = '/_sysadmin'}
        theme={theme}
        onToggleTheme={toggleTheme}
      />

      {/* Main Content Sections */}
      <main className="flex-grow">
        
        {/* 1. Hero Section */}
        <Hero
          currentLang={currentLang}
          onNavigate={handleNavigate}
          onQuickVerify={handleQuickVerify}
        />

        {/* 2. Domaines d'Expertise & Services */}
        <ServicesSection
          currentLang={currentLang}
          onSelectServiceForQuote={handleSelectServiceForDevis}
        />

        {/* 3. Formations Certifiantes & Programmes */}
        <TrainingCatalog
          currentLang={currentLang}
        />

        {/* 4. Portail de Vérification des Certificats Sécurisés */}
        <VerificationPortal
          currentLang={currentLang}
          initialQuery={initialVerifyId}
          onClearInitialQuery={() => setInitialVerifyId('')}
        />

        {/* 5. Le Cabinet & Biographie Expert Cherif Haddad */}
        <AboutSection
          currentLang={currentLang}
        />

        {/* 6. Témoignages & Confiance Industrielle */}
        <TestimonialsSection
          currentLang={currentLang}
        />

        {/* 7. Foire Aux Questions & Méthodologie (FAQ) */}
        <FAQSection
          currentLang={currentLang}
          onNavigateToContact={(subject) => {
            if (subject) setPrefilledService(subject);
            handleNavigate('contact');
          }}
          onNavigateToVerify={() => handleNavigate('verifier')}
        />

        {/* 8. Formulaire Contact & Devis Express */}
        <ContactSection
          currentLang={currentLang}
          prefilledService={prefilledService}
        />

      </main>

      {/* Global Footer */}
      <Footer
        currentLang={currentLang}
        onOpenLegal={setLegalModalType}
        onOpenAdmin={() => window.location.href = '/_sysadmin'}
      />

      {/* Floating Buttons */}
      <ScrollToTopButton currentLang={currentLang} />
      <WhatsAppButton currentLang={currentLang} />

      {/* MODAL: Legal Pages (Privacy / Terms) */}
      <LegalModal
        isOpen={legalModalType !== null}
        type={legalModalType}
        onClose={() => setLegalModalType(null)}
        currentLang={currentLang}
      />

    </div>
  );
}
